import React from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as InfoIcon } from '../../../../../../assets/icons/info-color-icon-ne.svg';
import BdsCircleIndicatorPictogram from '../../../../../../shared/components/v3/BdsCircleIndicator/BdsCircleIndicatorPictogram/BdsCircleIndicatorPictogram';
import BdsTooltip from '../../../../../../shared/components/v3/BdsTooltip/BdsTooltip';
import { TRAINING_EDUCATION_ICON } from '../../../../../../shared/constants/images';
import { FLEXIBLE_REMUNERATION_REQUEST_STATUS } from '../../../../../../shared/constants/requests';
import { TRANSLATIONS_REQUESTS } from '../../../../../../shared/translations/requests';
import { invertDateFormat } from '../../../../../../shared/utils/utils';

import './CourseRequestDetails.scss';

interface ICourseRequestDetails {
  collaborator: string;
  name: string;
  type: string;
  startDate: string;
  endDate: string;
  description: string;
  centerName: string;
  contactPerson: string;
  phone: string;
  contactEmail: string;
  status: string;
}

interface ICourseRequestDetailsProps {
  request: ICourseRequestDetails;
}

const CourseRequestDetails = ({ request }: ICourseRequestDetailsProps) => {
  const { t } = useTranslation();
  const datesValue = `${invertDateFormat(request.startDate.split('T')[0])} - ${invertDateFormat(
    request.endDate.split('T')[0]
  )}`;
  return (
    <div className="training-request-details" data-experience="new">
      <div className="trd-title">{t(TRANSLATIONS_REQUESTS.details.course.title)}</div>
      <div className="trd-content flex gap-10">
        <div className="trd-details flex flex-col gap-10">
          <div className="trd-grid">
            <div className="trd-grid-item">
              <p className="label">{t(TRANSLATIONS_REQUESTS.details.course.collaboratorLabel)}</p>
              <p className="value">{request.collaborator}</p>
            </div>
            <div className="trd-grid-item">
              <p className="label">{t(TRANSLATIONS_REQUESTS.details.course.trainingLabel)}</p>
              <p className="value">{request.name}</p>
            </div>
            <div className="trd-grid-item">
              <p className="label">{t(TRANSLATIONS_REQUESTS.details.course.typeLabel)}</p>
              <p className="value">{request.type}</p>
            </div>
            <div className="trd-grid-item">
              <p className="label">{t(TRANSLATIONS_REQUESTS.details.course.datesLabel)}</p>
              <p className="value">
                {request.status === FLEXIBLE_REMUNERATION_REQUEST_STATUS.cancelled.value ? (
                  <BdsTooltip
                    message={t(TRANSLATIONS_REQUESTS.details.course.tooltip)}
                    position="right"
                    variant="standard"
                  >
                    <div className="flex flex-row gap-1">
                      {datesValue}
                      <InfoIcon className="icon" />
                    </div>
                  </BdsTooltip>
                ) : (
                  <>{datesValue}</>
                )}
              </p>
            </div>
          </div>
          <div className="trd-description">
            <p className="label">{t(TRANSLATIONS_REQUESTS.details.course.descriptionLabel)}</p>
            <p className="value">{request.description}</p>
          </div>
          {request.status.toLowerCase() === FLEXIBLE_REMUNERATION_REQUEST_STATUS.pending.value && (
            <div className="trd-info-banner flex items-center gap-4">
              <InfoIcon className="icon" />
              <p className="value">{t(TRANSLATIONS_REQUESTS.details.course.infoBanner)}</p>
            </div>
          )}
        </div>
        <aside className="trd-sidebar flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <BdsCircleIndicatorPictogram pictogram={{ component: TRAINING_EDUCATION_ICON }} circleIndicatorSize="l" />
            <p className="trd-sidebar-title">{t(TRANSLATIONS_REQUESTS.details.course.trainingCenterLabel)}</p>
          </div>
          <div className="flex flex-col gap-8">
            <div className="trd-sidebar-item">
              <p className="label">{t(TRANSLATIONS_REQUESTS.details.course.nameLabel)}</p>
              <p className="value">{request.centerName}</p>
            </div>
            <div className="trd-sidebar-item">
              <p className="label">{t(TRANSLATIONS_REQUESTS.details.course.contactPersonLabel)}</p>
              <p className="value">{request.contactPerson}</p>
            </div>
            <div className="trd-sidebar-item">
              <p className="label">{t(TRANSLATIONS_REQUESTS.details.course.contactDataLabel)}</p>
              <p className="value">{request.phone}</p>
              <p className="value">{request.contactEmail}</p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CourseRequestDetails;
